import type { DetectionBox } from "@/components/phone-capture/capture-wizard/commons";
import logger from "@/lib/logging/logger";
import type { NativeCameraCapturePayload, NativeCameraPhotoPayload, NativeNormalizedRect } from "@/lib/native-bridge";
import { nativeCamera } from "./nativeCamera";

/** A still's frame, before or after it is taken. */
export type StillShape = {
	width: number;
	height: number;
	/** The part of the frame the slot showed, in the frame's own units; the overlay guides map onto it. */
	visibleBox: DetectionBox;
};

export type StillFrame = StillShape & { ctx: CanvasRenderingContext2D };

/** Where a full-resolution still comes from; the caller owns and releases the returned canvas. */
export type StillSource = {
	grab: () => Promise<StillFrame>;
	/**
	 * Set by a source that crops and encodes the still itself. `shape()` is the next still's frame
	 * with the photo as the unit square, so a box laid out in it is in fractions of the photo; null
	 * while the camera has not reported its geometry. `grab(crop)` answers with the finished JPEG,
	 * or with a decoded frame when the crop was not applied.
	 */
	delivered?: {
		shape: () => StillShape | null;
		grab: (crop?: DetectionBox) => Promise<Blob | StillFrame>;
	};
};

const fullBox = (width: number, height: number): DetectionBox => ({ x: 0, y: 0, w: width, h: height });

const createFrameContext = (width: number, height: number): CanvasRenderingContext2D => {
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Could not get 2D context.");
	return ctx;
};

export function browserStillSource(video: HTMLVideoElement): StillSource {
	return {
		async grab() {
			const ready =
				video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0;
			if (!ready) {
				logger.warning("INFO", "Video not ready for capture", {
					readyState: video.readyState,
					videoWidth: video.videoWidth,
					videoHeight: video.videoHeight,
				});
				throw new Error("Video not ready for capture");
			}
			const { videoWidth: width, videoHeight: height } = video;
			const ctx = createFrameContext(width, height);
			ctx.drawImage(video, 0, 0, width, height);
			return { ctx, width, height, visibleBox: fullBox(width, height) };
		},
	};
}

const jpegBytes = (jpegBase64: string): Uint8Array<ArrayBuffer> => {
	const binary = atob(jpegBase64);
	const bytes = new Uint8Array(binary.length);
	for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
	return bytes;
};

const hexOf = (bytes: Uint8Array): string => Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(" ");

// Wrapper builds before 20 crash on a non-finite number, so only a real box is ever posted.
const isRealBox = (box: DetectionBox): boolean =>
	[box.x, box.y, box.w, box.h].every(Number.isFinite) && box.w > 0 && box.h > 0;

// A delivered still is never decoded on the page, so its markers are all that vouches for it:
// a JPEG starts with FF D8 and ends with FF D9.
const isCompleteJpeg = (bytes: Uint8Array): boolean =>
	bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[bytes.length - 2] === 0xff && bytes[bytes.length - 1] === 0xd9;

/** The part of the upright photo the slot shows, once the wrapper has reported a usable one. */
const visibleRect = (): NativeNormalizedRect | null => {
	const visible = nativeCamera.getSnapshot().geometry?.previewVisibleRect;
	return visible && visible.width > 0 && visible.height > 0 ? visible : null;
};

async function frameOf(photo: NativeCameraPhotoPayload): Promise<StillFrame> {
	const bitmap = await createImageBitmap(new Blob([jpegBytes(photo.jpegBase64)], { type: "image/jpeg" }));
	try {
		const { width, height } = bitmap;
		const ctx = createFrameContext(width, height);
		ctx.drawImage(bitmap, 0, 0);
		const visible = visibleRect();
		const visibleBox = visible
			? { x: visible.x * width, y: visible.y * height, w: visible.width * width, h: visible.height * height }
			: fullBox(width, height);
		return { ctx, width, height, visibleBox };
	} finally {
		bitmap.close();
	}
}

/**
 * Still photos from the wrapper's camera. `grab` decodes the photo into the same canvas shape the
 * browser path yields; `delivered` has the wrapper crop and encode it, so the page never opens it.
 */
export function nativeStillSource(options: Omit<NativeCameraCapturePayload, "requestId" | "crop"> = {}): StillSource {
	return {
		grab: async () => frameOf(await nativeCamera.capture(options)),
		delivered: {
			shape() {
				const visible = visibleRect();
				if (!visible) return null;
				return {
					width: 1,
					height: 1,
					visibleBox: { x: visible.x, y: visible.y, w: visible.width, h: visible.height },
				};
			},
			async grab(crop) {
				const sent = crop && isRealBox(crop) ? crop : undefined;
				const photo = await nativeCamera.capture(
					sent ? { ...options, crop: { x: sent.x, y: sent.y, width: sent.w, height: sent.h } } : options,
				);
				if (crop && photo.cropped !== true) {
					// The crop was not sent, or the wrapper did not apply it: the page crops this whole photo.
					logger.warning("INFO", "Native still came back uncropped", {
						requestId: photo.requestId,
						width: photo.width,
						height: photo.height,
						cropSent: sent !== undefined,
					});
					return frameOf(photo);
				}
				const bytes = jpegBytes(photo.jpegBase64);
				if (!isCompleteJpeg(bytes)) {
					logger.warning("INFO", "Native still is not a complete JPEG", {
						requestId: photo.requestId,
						bytes: bytes.length,
						head: hexOf(bytes.slice(0, 2)),
						tail: hexOf(bytes.slice(-2)),
					});
					throw new Error("Native still is not a complete JPEG");
				}
				return new Blob([bytes], { type: "image/jpeg" });
			},
		},
	};
}
